<template>
  <div class="game-table-flush">
  <vue-good-table 
    styleClass="vgt-table condensed vgt-flush"
    v-bind="tableOptions"
    :columns="columns"
    :rows="rows"
    :pagination-options="paginationFor(rows)"
    :row-click="true"
    @row-click="onRowClick"
    compactMode>
    <template #table-row="props">
      <div v-if="props.column.field === 'ISINName'" class="d-flex align-items-center gap-2 py-0">
        <div
          class="d-flex align-items-center justify-content-center flex-shrink-0 portfolio-logo"
          :class="logoClass(props.row)"
        >
          <template v-if="props.row.ISIN">
            <img
              v-if="showShareLogo(props.row)"
              :src="shareLogoUrl(props.row.ISIN)"
              :alt="props.row.ISINName"
              @error="onLogoError(props.row.ISIN)"
            />
            <span v-else>{{ shareMonogram(props.row.ISINName) }}</span>
          </template>
          <span v-else>CZK</span>
        </div>
        <span class="text-sm font-weight-bold text-truncate">{{ props.row.ISINName }}</span>
        <span v-if="isLong(props.row)" class="side-badge is-long">LONG</span>
        <span v-else-if="isShort(props.row)" class="side-badge is-short">SHORT</span>
        <button
          v-if="props.row.ISINID"
          type="button"
          class="forum-bubble-btn"
          :class="{ 'forum-bubble-btn-dark': isDarkMode }"
          :title="'Fórum: ' + props.row.ISINName"
          @click.stop="openShareForum(props.row)"
        >
          <fa-icon icon="fa-regular fa-comment-dots" />
        </button>
      </div>
      <span
        v-else-if="props.column.field === 'ISINAmount'"
        class="qty-cell"
      >
        <template v-if="hasShareQty(props.row)">
          {{ props.formattedRow[props.column.field] }}<span class="qty-ks">ks</span>
        </template>
        <template v-else>{{ props.formattedRow[props.column.field] }}</template>
      </span>
      <span
        v-else-if="props.column.field === 'Profit'"
        class="qty-cell"
        :class="changeClass(props.row.Profit)"
      >
        {{ props.formattedRow[props.column.field] }}
      </span>
      <span v-else class="qty-cell">{{ props.formattedRow[props.column.field] }}</span>
    </template>
    <template #emptystate>
      <div class="text-center">
        <span>Žádná data</span>
      </div>
    </template>
  </vue-good-table>
  </div>
</template>

<script setup>
import { reactive, computed } from 'vue';
import { useStore } from 'vuex';
import { tableOptions, paginationFor } from '../../../../helpers/tableOptions';
import { shareLogoUrl, shareMonogram } from '@/helpers/shareLogos';

const props = defineProps({
  rows: Array
})

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const logoFailed = reactive({});

const formatFn = (value) => {
  return store.getters.formatTableNumber(value);
}

const changeClass = (value) => {
  const n = Number(value);
  if (Number.isNaN(n) || n === 0) return '';
  return n > 0 ? 'text-success' : 'text-danger';
}

const onLogoError = (isin) => {
  if (isin) logoFailed[isin] = true;
}

const showShareLogo = (row) => !!(shareLogoUrl(row.ISIN) && !logoFailed[row.ISIN]);

const positionQty = (row) => {
  const raw = row?.ISINAmount ?? row?.Amt;
  if (raw == null || raw === '') return 0;
  const n = Number(String(raw).replace(/\s+/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const isLong = (row) => positionQty(row) > 0;
const isShort = (row) => positionQty(row) < 0;
const hasShareQty = (row) => {
  if (!row?.ISIN) return false;
  const raw = row?.ISINAmount ?? row?.Amt;
  if (raw == null || raw === '') return false;
  return Number.isFinite(Number(String(raw).replace(/\s+/g, '').replace(',', '.')));
};

const logoClass = (row) => {
  if (!row.ISIN) return 'portfolio-logo-cash';
  if (!showShareLogo(row)) return 'portfolio-logo-fallback';
  return '';
};

const onRowClick = (params) => {
  if (params.row.ISINID) {
    store.dispatch('fetchISINChartData', params.row);
  }
}

const openShareForum = (row) => {
  if (!row?.ISINID) return;
  store.dispatch('openForum', {
    title: row.ISINName || 'Akcie',
    ISINID: row.ISINID,
    ISIN: row.ISIN
  });
}

const columns = reactive([
  {
    label: 'Aktivum',
    field: 'ISINName',
    tooltip: 'Název cenného papíru',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder',
    tdClass: 'text-sm font-weight-bold',
  },
  {
    label: 'Pozice',
    field: 'ISINAmount',
    type: 'number',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end',
  },
  {
    label: 'Ot. cena',
    field: 'ISINAvgPrice',
    tooltip: 'Otevření pozice',
    type: 'number',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end',
    formatFn: formatFn
  },
  {
    label: 'Akt. cena',
    field: 'ISINPrice',
    tooltip: 'Aktuální cena',
    type: 'number',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end',
    formatFn: formatFn
  },
  {
    label: 'Hodnota',
    field: 'PortfolioValue',
    tooltip: 'Tržní hodnota pozice / nevyužitý kapitál',
    type: 'number',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end',
    formatFn: formatFn
  },
  {
    label: 'Zisk',
    field: 'Profit',
    tooltip: 'Nerealizovaný zisk na pozici',
    type: 'number',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end',
    formatFn: formatFn
  },
]);

</script>

<style scoped>
.portfolio-logo {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  overflow: hidden;
}
.portfolio-logo img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}
.portfolio-logo-fallback {
  background: rgba(100, 116, 139, 0.16);
  color: #64748b;
  font-size: 0.55rem;
  font-weight: 700;
}
.portfolio-logo-cash {
  width: 20px;
  height: 20px;
  overflow: visible;
  background: rgba(5, 150, 105, 0.16);
  color: #059669;
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: -0.06em;
  line-height: 1;
}

.forum-bubble-btn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid #CBD5E1;
  background: transparent;
  color: #64748B;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.7rem;
}

.forum-bubble-btn:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.08);
}

.forum-bubble-btn-dark {
  border-color: #475569;
  color: #94A3B8;
}

.forum-bubble-btn-dark:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.14);
}

.side-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.12rem 0.4rem;
  border-radius: 999px;
  line-height: 1.2;
}

.side-badge.is-long {
  background: rgba(16, 185, 129, 0.14);
  color: #059669;
}

.side-badge.is-short {
  background: rgba(255, 138, 76, 0.16);
  color: #EA580C;
}

:deep(.vgt-table td) {
  padding-top: 0.35rem !important;
  padding-bottom: 0.35rem !important;
}

.game-table-flush :deep(.vgt-table),
:deep(.vgt-table.vgt-flush) {
  border: none !important;
}

.game-table-flush :deep(.vgt-table tbody tr:last-child td),
:deep(.vgt-table tbody tr:last-child td) {
  border-bottom: none !important;
}

.game-table-flush :deep(.vgt-wrap__footer),
.game-table-flush :deep(.vgt-wrap__actions-footer),
:deep(.vgt-wrap__footer) {
  display: none !important;
  height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  overflow: hidden !important;
}

.game-table-flush :deep(.vgt-responsive) {
  margin-bottom: 0 !important;
}

.qty-ks {
  margin-left: 0.22em;
  font-weight: 600;
  font-size: 0.82em;
  opacity: 0.75;
}

.qty-cell {
  display: block;
  width: 100%;
  text-align: right;
  white-space: nowrap;
}
</style>
